import {
  FieldError,
  InfoIconPopover,
  InfoPopover,
  Modal,
  NumberField,
  Select,
  TrackedButton,
  Typography,
} from '@automata/ui';
import { PlannerOptionsInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';

import {
  HARDCODED_PRESET_OPTIONS,
  PLANNER_INFO_POPOVER,
  errorMessage,
} from './constants';
import {
  selectLastPlanningTime,
  selectPlannerOptions,
} from '../../../../features/workflow/selectors';
import { updatePlannerOptions } from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

type Props = {
  onClose: () => void;
};

type ErrorsProps = {
  target_objective?: string | null;
  max_computation_time?: string | null;
  max_dead_time?: string | null;
};

const fieldsForValidation = [
  'target_objective',
  'max_computation_time',
  'max_dead_time',
];

const validateField = (value: number | null) => {
  return typeof value === 'number' && value < 0 ? errorMessage : null;
};

export const EditPlannerOptionsModal = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const previousPlannerOptions = useAppSelector(selectPlannerOptions);
  const updateLastPlanningTime = useAppSelector(
    selectLastPlanningTime
  )?.toFixed(2);

  const [plannerOptions, setPlannerOptions] = useState<PlannerOptionsInput>(
    previousPlannerOptions ?? {}
  );
  const [errors, setErrors] = useState<ErrorsProps>({});

  const isInitial = useMemo(() => {
    return (
      JSON.stringify(plannerOptions) ===
      JSON.stringify(previousPlannerOptions ?? {})
    );
  }, [plannerOptions, previousPlannerOptions]);

  const planningTimeLabel = updateLastPlanningTime
    ? `Last recorded planning time: ${updateLastPlanningTime}s`
    : 'No reference time available until a plan has been generated.';

  const handleUpdatePlannerOption = (
    field: keyof PlannerOptionsInput,
    value: string | number | boolean | null
  ) => {
    if (fieldsForValidation.includes(field)) {
      setErrors((prev) => ({
        ...prev,
        [field]: validateField(value as number | null),
      }));
    }

    setPlannerOptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSavePlannerOptions = () => {
    dispatch(updatePlannerOptions(plannerOptions));
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Planner Options">
      <Stack spacing={2} mt={1}>
        <Box>
          <NumberField
            decimal={true}
            error={!!errors.target_objective}
            fullWidth
            infoPopover={
              <InfoPopover
                title={PLANNER_INFO_POPOVER.target_objective.title}
              />
            }
            label="target makespan"
            min={0}
            value={plannerOptions.target_objective}
            onChange={(e) => {
              handleUpdatePlannerOption('target_objective', e);
            }}
          />
          <FieldError error={errors.target_objective} />
        </Box>
        <Box>
          <NumberField
            error={!!errors.max_computation_time}
            fullWidth
            infoPopover={
              <InfoPopover
                title={PLANNER_INFO_POPOVER.max_computation_time.title}
              />
            }
            label="max computation time"
            min={0}
            value={plannerOptions.max_computation_time}
            onChange={(e) => {
              handleUpdatePlannerOption('max_computation_time', e);
            }}
          />
          <FieldError error={errors.max_computation_time} />
          <Typography mt={1} color={theme.palette.text.secondary}>
            {planningTimeLabel}
          </Typography>
        </Box>
        <Box>
          <NumberField
            error={!!errors.max_dead_time}
            fullWidth
            infoPopover={
              <InfoPopover title={PLANNER_INFO_POPOVER.max_dead_time.title} />
            }
            label="max dead time"
            min={0}
            value={plannerOptions.max_dead_time}
            onChange={(e) => {
              handleUpdatePlannerOption('max_dead_time', e);
            }}
          />
          <FieldError error={errors.max_dead_time} />
        </Box>
        <Select
          label="preset"
          value={plannerOptions.preset ?? ''}
          infoPopover={
            <InfoPopover
              title={PLANNER_INFO_POPOVER.preset.title}
              copy={PLANNER_INFO_POPOVER.preset.copy}
            />
          }
          onChange={(e) => {
            handleUpdatePlannerOption(
              'preset',
              e.target.value === 'NONE'
                ? null
                : (e.target.value as string | null)
            );
          }}
        >
          {HARDCODED_PRESET_OPTIONS.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
        <Stack>
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Typography variant="overline">
              round robin load balancing
            </Typography>
            <InfoIconPopover
              infoPopover={
                <InfoPopover
                  title={PLANNER_INFO_POPOVER.round_robin_load_balancing.title}
                />
              }
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                id="canvas-parameterReference-switch"
                checked={!!plannerOptions.round_robin_load_balancing}
                onChange={(e) =>
                  handleUpdatePlannerOption(
                    'round_robin_load_balancing',
                    e.target.checked
                  )
                }
              />
            }
            label={plannerOptions.round_robin_load_balancing ? 'True' : 'False'}
          />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="workflowBuilder.editPlannerOptionsModal.Cancel"
            color="primary"
            variant="text"
            onClick={onClose}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            trackLabel="workflowBuilder.editPlannerOptionsModal.Save"
            color="primary"
            variant="contained"
            onClick={handleSavePlannerOptions}
            disabled={isInitial || Object.values(errors).some(Boolean)}
          >
            Apply
          </TrackedButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
